<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Interview recordings
      </oxd-text>
      <oxd-divider />
      <oxd-form @submit-valid="search">
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.candidateName"
              label="Candidate"
              placeholder="Type a name"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <vacancy-dropdown v-model="filters.vacancy" />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.slot"
              type="select"
              label="Interview"
              :options="slotOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.source"
              type="select"
              label="Call"
              :options="sourceOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-form-actions>
          <oxd-button display-type="ghost" label="Reset" @click="reset" />
          <oxd-button display-type="secondary" label="Search" type="submit" />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div class="orangehrm-card-container rec-list">
      <oxd-text class="rec-count">
        {{
          loading && !calls.length
            ? 'Loading…'
            : `${total} ${total === 1 ? 'call' : 'calls'}`
        }}
      </oxd-text>
      <div v-for="call in calls" :key="call.callId" class="rec-row">
        <div class="rec-main">
          <div class="rec-title">
            <a :href="candidateUrl(call)" class="rec-candidate">
              {{ call.candidate.name }}
            </a>
            <span class="rec-meta">
              {{
                [
                  call.vacancy,
                  call.slot === 'technical'
                    ? 'Technical interview (Alex)'
                    : 'HR interview (Anna)',
                ]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </span>
          </div>
          <div class="rec-sub">
            <span :class="['rec-source', `rec-source--${call.source}`]">
              {{ sourceLabel(call.source) }}
            </span>
            <span>{{ when(call.startedAt) }}</span>
            <span v-if="call.durationMinutes">
              {{ call.durationMinutes }} min
            </span>
            <span v-if="call.score !== null && call.score !== undefined">
              Score {{ call.score }}/10
            </span>
          </div>
          <p v-if="call.summary" class="rec-summary">{{ call.summary }}</p>
        </div>
        <div class="rec-player">
          <audio
            v-if="call.hasRecording"
            controls
            preload="none"
            :src="recordingUrl(call)"
          ></audio>
          <span v-else class="rec-none">No recording</span>
          <div class="rec-links">
            <a :href="candidateUrl(call)">Open the interview</a>
            <a v-if="call.hasRecording" :href="recordingUrl(call)" download>
              Download
            </a>
          </div>
        </div>
      </div>
      <oxd-text v-if="!loading && !calls.length" class="rec-count">
        No recordings yet. They appear here after an interview with Anna or
        Alex, in a browser test or a GoTo, Jitsi or Google Meet meeting.
      </oxd-text>
      <oxd-button
        v-if="calls.length < total"
        display-type="ghost"
        :label="loading ? 'Loading…' : 'Show more'"
        :disabled="loading"
        class="rec-more"
        @click="load(false)"
      />
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import VacancyDropdown from '@/orangehrmRecruitmentPlugin/components/VacancyDropdown';

const PAGE = 30;
const emptyFilters = () => ({
  candidateName: '',
  vacancy: null,
  slot: null,
  source: null,
});

export default {
  name: 'InterviewRecordings',

  components: {
    'vacancy-dropdown': VacancyDropdown,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/recordings',
    );
    return {http};
  },

  data() {
    return {
      filters: emptyFilters(),
      calls: [],
      total: 0,
      loading: false,
      slotOptions: [
        {id: 'general', label: 'HR interview (Anna)'},
        {id: 'technical', label: 'Technical interview (Alex)'},
      ],
      sourceOptions: [
        {id: 'goto', label: 'GoTo meeting'},
        {id: 'jitsi', label: 'Jitsi meeting'},
        {id: 'meet', label: 'Google Meet meeting'},
        {id: 'browser', label: 'Browser test'},
      ],
    };
  },

  created() {
    this.load(true);
  },

  methods: {
    sourceLabel(source) {
      return this.sourceOptions.find((o) => o.id === source)?.label || source;
    },
    load(fresh) {
      this.loading = true;
      const params = {limit: PAGE, offset: fresh ? 0 : this.calls.length};
      if (this.filters.candidateName.trim()) {
        params.candidateName = this.filters.candidateName.trim();
      }
      if (this.filters.vacancy) params.vacancyId = this.filters.vacancy.id;
      if (this.filters.slot) params.slot = this.filters.slot.id;
      if (this.filters.source) params.source = this.filters.source.id;
      this.http
        .getAll(params)
        .then(({data}) => {
          this.calls = fresh ? data.data : [...this.calls, ...data.data];
          this.total = data.meta.total;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    search() {
      this.load(true);
    },
    reset() {
      this.filters = emptyFilters();
      this.load(true);
    },
    // straight onto that interview's tab, with this call picked
    candidateUrl(call) {
      const tab = call.slot === 'technical' ? 'tech' : 'hr';
      return `${window.appGlobal.baseUrl}/recruitment/addCandidate/${
        call.candidate.id
      }?tab=${tab}&call=${encodeURIComponent(call.callId)}`;
    },
    recordingUrl(call) {
      return `${window.appGlobal.baseUrl}/anna/recording/${call.candidate.id}/${
        call.slot
      }?call=${encodeURIComponent(call.callId)}`;
    },
    when(value) {
      return value ? String(value).slice(0, 16) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.rec-list {
  margin-top: 16px;
}
.rec-count {
  color: #6a7482;
  font-size: 13px;
  padding-bottom: 8px;
}
.rec-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  justify-content: space-between;
  padding: 14px 0;
  border-top: 1px solid #eef1f5;
}
.rec-main {
  flex: 1 1 360px;
  min-width: 0;
}
.rec-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
}
.rec-candidate {
  font-weight: 700;
  color: #12233f;
  text-decoration: none;
  &:hover {
    color: var(--oxd-primary-one-color);
    text-decoration: underline;
  }
}
.rec-meta,
.rec-sub {
  font-size: 13px;
  color: #64728c;
}
.rec-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
  margin-top: 4px;
}
.rec-source {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  &--goto {
    background: #e3f3e6;
    color: #2e7d32;
  }
  &--meet {
    background: #fef4e2;
    color: #a05a00;
  }
  &--jitsi {
    background: #e6effb;
    color: #1f5fa8;
  }
  &--browser {
    background: #eef1f5;
    color: #64728c;
  }
}
.rec-summary {
  margin: 6px 0 0;
  font-size: 13px;
  color: #3d4757;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rec-player {
  flex: 0 1 340px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  audio {
    width: 100%;
  }
}
.rec-none {
  font-size: 13px;
  color: #8a94a6;
}
.rec-links {
  display: flex;
  gap: 16px;
  font-size: 13px;
  a {
    color: var(--oxd-primary-one-color);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}
.rec-more {
  margin-top: 12px;
}
</style>
